import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Copy, ExternalLink } from 'lucide-react';
import type { LabeledVizSpec } from 'shared/src/api/ai-labeled-viz-types';
import type { NoDataViz } from 'shared/src/api/ai-viz-types';
import type { CubeQuerySchema } from 'shared/src/api/cube-query-schema';
import { cubeToCubeSql } from 'shared/src/utils/cube/cube-sql-converter';
import { allCubeMeta } from 'shared/src/generated/cube-meta/cube-meta-registry';
import type { CubeMemberInfo } from 'shared/src/trpc-types/cube-load-trpc-types';
import { showErrorToast } from '@/components/feedback/error-toast';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/forms/ui/tooltip';
import { memberTitle } from './cube-member-utils';
import { MemberBadge } from './member-badge';
import { collapseMotion } from './motion-presets';
import { VizSummary } from './viz-summary';
import { VizTypeBadge } from './viz-type-badge';

type CubeQueryCardProps = {
  query: CubeQuerySchema;
  memberMeta?: Record<string, CubeMemberInfo>;
  isLoading: boolean;
  mode?: 'expanded' | 'compact';
  onToggle?: () => void;
  vizUrl?: string;
  labeledViz?: LabeledVizSpec | NoDataViz;
  hideCopyButtons?: boolean;
};

type Section = {
  title: string;
  items: React.ReactNode[];
};

export function CubeQueryCard({
  query,
  memberMeta,
  isLoading,
  mode = 'expanded',
  onToggle,
  vizUrl,
  labeledViz,
  hideCopyButtons
}: CubeQueryCardProps) {
  const sections = buildSections(query, memberMeta);
  const [copied, setCopied] = React.useState(false);
  const [copiedQuery, setCopiedQuery] = React.useState(false);
  const [copiedSql, setCopiedSql] = React.useState(false);

  const handleOpenViz = React.useCallback(() => {
    if (!vizUrl) return;
    window.open(vizUrl, '_blank');
    navigator.clipboard.writeText(vizUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [vizUrl, setCopied]);

  const handleCopyQuery = React.useCallback(() => {
    navigator.clipboard.writeText(JSON.stringify(query, null, 2));
    setCopiedQuery(true);
    setTimeout(() => setCopiedQuery(false), 1500);
  }, [query, setCopiedQuery]);

  const handleCopySql = React.useCallback(() => {
    try {
      const sql = cubeToCubeSql(query, allCubeMeta);
      navigator.clipboard.writeText(sql);
      setCopiedSql(true);
      setTimeout(() => setCopiedSql(false), 1500);
    } catch {
      showErrorToast('Failed to generate SQL');
    }
  }, [query, setCopiedSql]);

  return (
    <div className="space-y-2 p-3">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold uppercase text-muted-foreground">Cube Query</span>
        {onToggle ? (
          <button
            type="button"
            onClick={onToggle}
            className="flex items-center gap-1 rounded px-1.5 text-[11px] leading-none text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
            <ChevronDown
              className={`h-3 w-3 transition-transform duration-200 ${mode === 'compact' ? '-rotate-90' : ''}`}
            />
            {mode === 'compact' ? 'Show details' : 'Hide details'}
          </button>
        ) : null}
        {isLoading ? (
          <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
        ) : null}
        {vizUrl ? (
          <div className="ml-auto">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={handleOpenViz}
                  className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground">
                  {copied ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    <ExternalLink className="h-3.5 w-3.5" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent>View visualization</TooltipContent>
            </Tooltip>
          </div>
        ) : null}
      </div>

      <AnimatePresence mode="wait" initial={false}>
        {mode === 'expanded' ? (
          <motion.div key="expanded" {...collapseMotion}>
            <div className="flex items-start gap-4">
              <div className="min-w-0 flex-1">
                {sections.map(section => (
                  <div key={section.title} className="space-y-1 pt-1">
                    <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                      {section.title}
                    </div>
                    <div className="flex flex-wrap gap-1">{section.items}</div>
                  </div>
                ))}
                {query.limit ? (
                  <div className="space-y-1 pt-1">
                    <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                      Limit
                    </div>
                    <span className="inline-flex items-center rounded-full bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700">
                      {query.limit.toLocaleString()} rows
                    </span>
                  </div>
                ) : null}
              </div>
              {labeledViz ? (
                <div className="w-48 shrink-0">
                  <VizSummary labeledViz={labeledViz} memberMeta={memberMeta} />
                </div>
              ) : null}
            </div>
            {hideCopyButtons ? null : (
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={handleCopyQuery}
                  className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                  {copiedQuery ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  {copiedQuery ? 'Copied!' : 'Copy Query'}
                </button>
                <button
                  type="button"
                  onClick={handleCopySql}
                  className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                  {copiedSql ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  {copiedSql ? 'Copied!' : 'Copy SQL'}
                </button>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div key="compact" {...collapseMotion}>
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <CompactQuerySummary query={query} memberMeta={memberMeta} />
              </div>
              {labeledViz ? (
                <div className="shrink-0">
                  <VizTypeBadge vizType={labeledViz.vizType} />
                </div>
              ) : null}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function buildSections(
  query: CubeQuerySchema,
  memberMeta?: Record<string, CubeMemberInfo>
): Section[] {
  const sections: Section[] = [];

  if (query.measures?.length) {
    sections.push({
      title: 'Measures',
      items: query.measures.map(ref => (
        <MemberBadge key={ref} memberRef={ref} info={memberMeta?.[ref]} />
      ))
    });
  }

  if (query.dimensions?.length) {
    sections.push({
      title: 'Dimensions',
      items: query.dimensions.map(ref => (
        <MemberBadge key={ref} memberRef={ref} info={memberMeta?.[ref]} />
      ))
    });
  }

  if (query.timeDimensions?.length) {
    sections.push({
      title: 'Time',
      items: query.timeDimensions.map(td => {
        const label = memberTitle(td.dimension, memberMeta);
        const rangeStr = formatDateRange(td.dateRange, td.granularity);

        return (
          <span
            key={td.dimension}
            className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:ring-amber-800">
            {label}
            {rangeStr ? (
              <span className="text-amber-500 dark:text-amber-400">({rangeStr})</span>
            ) : null}
          </span>
        );
      })
    });
  }

  if (query.filters?.length) {
    sections.push({
      title: 'Filters',
      items: query.filters.map((f, i) => {
        const label = memberTitle(f.member, memberMeta);
        const valuesStr = f.values?.join(', ') ?? '';

        return (
          <span
            key={`${f.member}-${i}`}
            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:ring-emerald-800">
            {label}
            <span className="text-emerald-500 dark:text-emerald-400">
              {f.operator}
              {valuesStr ? ` ${valuesStr}` : ''}
            </span>
          </span>
        );
      })
    });
  }

  if (query.order?.length) {
    sections.push({
      title: 'Order',
      items: query.order.map(([member, direction]) => {
        const label = memberTitle(member, memberMeta);
        return (
          <span
            key={member}
            className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700 ring-1 ring-inset ring-rose-200 dark:bg-rose-950 dark:text-rose-300 dark:ring-rose-800">
            {label}
            <span className="text-rose-500 dark:text-rose-400">
              {direction === 'asc' ? '↑' : '↓'}
            </span>
          </span>
        );
      })
    });
  }

  return sections;
}

function CompactQuerySummary({
  query,
  memberMeta
}: {
  query: CubeQuerySchema;
  memberMeta?: Record<string, CubeMemberInfo>;
}) {
  const segments: React.ReactNode[] = [];

  if (query.measures?.length) {
    segments.push(
      <span key="measures" className="text-blue-600">
        {query.measures.map(ref => memberTitle(ref, memberMeta)).join(', ')}
      </span>
    );
  }

  if (query.dimensions?.length) {
    segments.push(
      <span key="dimensions" className="text-violet-600">
        {query.dimensions.map(ref => memberTitle(ref, memberMeta)).join(', ')}
      </span>
    );
  }

  if (query.timeDimensions?.length) {
    const td = query.timeDimensions[0];
    const dateStr = formatDateRange(td.dateRange, td.granularity);
    if (dateStr) {
      segments.push(
        <span key="time" className="text-amber-600">
          {dateStr}
        </span>
      );
    }
  }

  if (query.filters?.length) {
    const filterStrs = query.filters.map(f => {
      const label = memberTitle(f.member, memberMeta);
      const valuesStr = f.values?.join(', ') ?? '';
      return `${label} ${f.operator}${valuesStr ? ` ${valuesStr}` : ''}`;
    });
    segments.push(
      <span key="filters" className="text-emerald-600">
        {filterStrs.join(', ')}
      </span>
    );
  }

  if (query.order?.length) {
    const orderStrs = query.order.map(
      ([member, direction]) =>
        `${memberTitle(member, memberMeta)} ${direction === 'asc' ? '↑' : '↓'}`
    );
    segments.push(
      <span key="order" className="text-rose-600">
        {orderStrs.join(', ')}
      </span>
    );
  }

  const fullText = segments.reduce<React.ReactNode[]>((acc, seg, i) => {
    if (i > 0)
      acc.push(
        <span key={`sep-${i}`} className="text-muted-foreground">
          {' '}
          ·{' '}
        </span>
      );
    acc.push(seg);
    return acc;
  }, []);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="line-clamp-1 text-xs">{fullText}</div>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-md text-left text-xs">
        <div className="flex flex-wrap gap-1">{fullText}</div>
      </TooltipContent>
    </Tooltip>
  );
}

const dateFmt = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric'
});

function formatDateRange(dateRange?: string[] | string, granularity?: string): string {
  const parts: string[] = [];

  if (Array.isArray(dateRange) && dateRange.length >= 2) {
    const start = new Date(dateRange[0] + 'T00:00:00');
    const end = new Date(dateRange[1] + 'T00:00:00');
    if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
      parts.push(`${dateFmt.format(start)} – ${dateFmt.format(end)}`);
    } else {
      parts.push(`${dateRange[0]} – ${dateRange[1]}`);
    }
  } else if (Array.isArray(dateRange) && dateRange.length === 1) {
    parts.push(dateRange[0]);
  } else if (typeof dateRange === 'string') {
    parts.push(dateRange);
  }

  if (granularity) parts.push(`by ${granularity}`);
  return parts.join(' ');
}
